import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingPageShell from './MarketingPageShell';
import { PREORDER_GOOGLE_FORM_URL, PREORDER_CONTACT_EMAIL } from '../config/marketing';
import './MarketingPages.css';

const BIKE_OPTIONS = [
  { value: '', label: 'Select bike category' },
  { value: 'trail', label: 'Trail / all-mountain' },
  { value: 'enduro', label: 'Enduro' },
  { value: 'dh', label: 'Downhill / bike park' },
  { value: 'xc', label: 'XC / light trail' },
  { value: 'ebike', label: 'E-MTB' },
  { value: 'other', label: 'Other (describe below)' },
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  address: '',
  bikeCategory: '',
  bikeNotes: '',
};

function PreOrderPage() {
  const [form, setForm] = useState(initialForm);
  const [submitted, setSubmitted] = useState(false);

  const embedUrl = PREORDER_GOOGLE_FORM_URL
    ? PREORDER_GOOGLE_FORM_URL.includes('?')
      ? `${PREORDER_GOOGLE_FORM_URL}&embedded=true`
      : `${PREORDER_GOOGLE_FORM_URL}?embedded=true`
    : '';

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const openMailDraft = () => {
    const lines = [
      'ShokIQ pre-order request',
      '',
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Phone: ${form.phone}`,
      `Address: ${form.address}`,
      `Bike category: ${form.bikeCategory}`,
      `Bike / setup notes: ${form.bikeNotes}`,
    ];
    const subject = encodeURIComponent('ShokIQ pre-order');
    const body = encodeURIComponent(lines.join('\n'));
    window.location.href = `mailto:${PREORDER_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      return;
    }
    openMailDraft();
  };

  return (
    <MarketingPageShell>
      <section className="marketing-hero">
        <h1>Pre-order ShokIQ</h1>
        <p className="lede">
          Reserve the full system: logger, sensors, mounts, and access to the ShokIQ analysis platform. Tell us how to reach you and
          what you ride—we will follow up to confirm details and fulfillment.
        </p>
      </section>

      <div className="marketing-inner">
        <Link to="/" className="marketing-back">
          ← Back to home
        </Link>

        <div className="marketing-card marketing-card--package-with-visual">
          <div className="marketing-card-split">
            <div className="marketing-card-split__body">
              <h2>Complete package — $599</h2>
              <p className="preorder-price">$599</p>
              <p>Includes:</p>
              <ul className="marketing-includes">
                <li>ShokIQ data logger</li>
                <li>Two linear potentiometers (fork and shock travel)</li>
                <li>Mounting hardware for a typical MTB setup</li>
                <li>Access to the ShokIQ analysis portal for telemetry, stats, overlays, and comparisons</li>
              </ul>
              <p className="hint" style={{ marginTop: '1rem' }}>
                Pre-order is not charged on this page; we use your details to contact you with next steps. For questions,{' '}
                <Link to="/support">contact support</Link>.
              </p>
            </div>
            <div className="marketing-card-split__media">
              <img
                src={`${process.env.PUBLIC_URL || ''}/img/product/shokiq_product_topview.png`}
                alt="ShokIQ complete kit: data logger, two linear travel sensors, and cabling (top view)"
                className="marketing-card-split__img"
              />
            </div>
          </div>
        </div>

        {PREORDER_GOOGLE_FORM_URL ? (
          <div className="marketing-card">
            <h2>Pre-order form</h2>
            <p>
              Complete the short Google Form below (name, address, email, contact info, and bike type). If it does not load,{' '}
              <a href={PREORDER_GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer">
                open the form in a new tab
              </a>
              .
            </p>
            <div className="preorder-iframe-wrap">
              <iframe title="ShokIQ pre-order" src={embedUrl} />
            </div>
          </div>
        ) : (
          <div className="marketing-card">
            <h2>Request a pre-order</h2>
            <p className="hint">
              We will open your email app with this information so you can send it to {PREORDER_CONTACT_EMAIL}. To use a Google Form
              instead, set <code>REACT_APP_PREORDER_GOOGLE_FORM_URL</code> in your environment and rebuild.
            </p>
            <form className="preorder-form" onSubmit={onSubmit}>
              <label htmlFor="po-name">Full name *</label>
              <input id="po-name" name="name" value={form.name} onChange={onChange} autoComplete="name" required />

              <label htmlFor="po-email">Email *</label>
              <input id="po-email" name="email" type="email" value={form.email} onChange={onChange} autoComplete="email" required />

              <label htmlFor="po-phone">Phone / WhatsApp</label>
              <input id="po-phone" name="phone" type="tel" value={form.phone} onChange={onChange} autoComplete="tel" />

              <label htmlFor="po-address">Shipping address</label>
              <textarea id="po-address" name="address" value={form.address} onChange={onChange} autoComplete="street-address" />

              <label htmlFor="po-bike">Bike type</label>
              <select id="po-bike" name="bikeCategory" value={form.bikeCategory} onChange={onChange}>
                {BIKE_OPTIONS.map((o) => (
                  <option key={o.value || 'empty'} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>

              <label htmlFor="po-notes">Bike model, travel, or notes (optional)</label>
              <textarea id="po-notes" name="bikeNotes" value={form.bikeNotes} onChange={onChange} />

              <div className="preorder-actions">
                <button type="submit" className="primary-btn">
                  Email pre-order request
                </button>
                <Link to="/support" className="auth-link auth-btn outlined" style={{ margin: 0 }}>
                  Questions?
                </Link>
              </div>
            </form>
            {submitted ? (
              <p className="hint" style={{ marginTop: '1rem' }}>
                If your mail client did not open, copy the details above and email {PREORDER_CONTACT_EMAIL}.
              </p>
            ) : null}
          </div>
        )}
      </div>
    </MarketingPageShell>
  );
}

export default PreOrderPage;
